import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Bell,
  CheckCircle,
  MessageSquare,
  Send,
  AlertCircle,
  Clock,
  CheckCheck,
  Filter,
  BellRing,
  BellOff,
  Smartphone,
  TestTube
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';
import usePushNotifications from '../hooks/usePushNotifications';

const NOTIFICATION_ICONS = {
  approval_requested: Clock,
  approved: CheckCircle,
  rejected: AlertCircle,
  comment: MessageSquare,
  published: Send,
  failed: AlertCircle,
  reminder: Bell
};

const NOTIFICATION_STYLES = {
  approval_requested: { bg: 'bg-amber-50', icon: 'text-amber-500', border: 'border-amber-200' },
  approved: { bg: 'bg-emerald-50', icon: 'text-emerald-500', border: 'border-emerald-200' },
  rejected: { bg: 'bg-red-50', icon: 'text-red-500', border: 'border-red-200' },
  comment: { bg: 'bg-blue-50', icon: 'text-blue-500', border: 'border-blue-200' },
  published: { bg: 'bg-green-50', icon: 'text-green-500', border: 'border-green-200' },
  failed: { bg: 'bg-red-50', icon: 'text-red-500', border: 'border-red-200' },
  reminder: { bg: 'bg-purple-50', icon: 'text-purple-500', border: 'border-purple-200' }
};

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    error: pushError,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    sendTest: sendTestPush
  } = usePushNotifications();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePush = async () => {
    if (pushSubscribed) {
      const success = await unsubscribePush();
      if (success) {
        toast.success('Push notifications disabled');
      } else {
        toast.error('Failed to disable push notifications');
      }
    } else {
      const success = await subscribePush();
      if (success) {
        toast.success('Push notifications enabled!');
      } else if (pushPermission === 'denied') {
        toast.error('Please enable notifications in your browser settings');
      } else {
        toast.error('Failed to enable push notifications');
      }
    }
  };

  const handleTestPush = async () => {
    try {
      const result = await sendTestPush();
      if (result.sent > 0) {
        toast.success('Test notification sent!');
      } else {
        toast.info('No devices registered for push notifications');
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n =>
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications;

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="notifications-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Bell className="h-6 w-6 text-emerald-500" />
              Notifications
              {unreadCount > 0 && (
                <Badge className="bg-red-500 text-white">{unreadCount}</Badge>
              )}
            </h1>
            <p className="text-slate-600 mt-1">Stay updated with your content activity</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
              data-testid="push-settings-btn"
            >
              <BellRing className="h-4 w-4 mr-2" />
              Push Settings
            </Button>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[130px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
              </SelectContent>
            </Select>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                onClick={handleMarkAllAsRead}
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="h-4 w-4 mr-2" />
                Mark all read
              </Button>
            )}
          </div>
        </div>

        {/* Push Notification Settings */}
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6"
          >
            <Card className="border-emerald-100 bg-emerald-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Smartphone className="h-5 w-5 text-emerald-600" />
                  Push Notification Settings
                </CardTitle>
                <CardDescription>
                  Get instant notifications on your device when important events happen
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!pushSupported ? (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <BellOff className="h-5 w-5 text-amber-600" />
                    <p className="text-sm text-amber-800">
                      Push notifications are not supported in this browser
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <label className="text-sm font-medium text-slate-900">
                          Browser Push Notifications
                        </label>
                        <p className="text-xs text-slate-500">
                          {pushSubscribed 
                            ? 'You will receive notifications even when the app is closed'
                            : 'Enable to receive notifications in your browser'}
                        </p>
                      </div>
                      <Switch
                        checked={pushSubscribed}
                        onCheckedChange={handleTogglePush}
                        disabled={pushLoading}
                        data-testid="push-toggle"
                      />
                    </div>
                    
                    {pushPermission === 'denied' && (
                      <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                        <AlertCircle className="h-5 w-5 text-red-600" />
                        <p className="text-sm text-red-800">
                          Notifications are blocked. Please enable them in your browser settings.
                        </p>
                      </div>
                    )}
                    
                    {pushSubscribed && (
                      <div className="flex items-center gap-3 pt-2 border-t border-emerald-100">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleTestPush}
                          className="border-emerald-200 hover:bg-emerald-50"
                          data-testid="test-push-btn"
                        >
                          <TestTube className="h-4 w-4 mr-2" />
                          Send Test Notification
                        </Button>
                        <span className="text-xs text-slate-500">
                          Test if notifications are working
                        </span>
                      </div>
                    )}
                    
                    {pushError && (
                      <p className="text-sm text-red-600">{pushError}</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Notifications List */}
        <Card className="border-slate-100">
          <CardContent className="p-0">
            {filteredNotifications.length === 0 ? (
              <div className="text-center py-16">
                <Bell className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                </h3>
                <p className="text-slate-500">
                  {filter === 'unread'
                    ? 'You\'re all caught up!'
                    : 'You\'ll see notifications about your content here'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredNotifications.map((notification, i) => {
                  const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
                  const style = NOTIFICATION_STYLES[notification.type] || NOTIFICATION_STYLES.reminder;

                  return (
                    <motion.div
                      key={notification.notification_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className={`p-4 flex items-start gap-4 hover:bg-slate-50 transition-colors ${
                        !notification.read ? 'bg-slate-50/50' : ''
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full ${style.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`h-5 w-5 ${style.icon}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className={`font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-700'}`}>
                              {notification.title}
                            </p>
                            <p className="text-sm text-slate-600 mt-0.5">{notification.message}</p>
                          </div>
                          {!notification.read && (
                            <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0 mt-2" />
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs text-slate-500">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </span>
                          {notification.link && (
                            <Link
                              to={notification.link}
                              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              View details →
                            </Link>
                          )}
                          {!notification.read && (
                            <button
                              onClick={() => handleMarkAsRead(notification.notification_id)}
                              className="text-xs text-slate-500 hover:text-slate-700"
                            >
                              Mark as read
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default NotificationsPage;
